import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faHandshake, faLeaf, faGem } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Container, Section } from '../styles/components';
import PageHero from '../components/layout/PageHero';

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: ${theme.colors.gradient.gold};
    margin: ${theme.spacing.sm} auto 0;
    border-radius: ${theme.borderRadius.small};
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const StorySection = styled(Section)`
  padding: ${theme.spacing.xxl} 0;
`;

const StoryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  max-width: 800px;
  margin: 0 auto;
`;

const StoryParagraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: ${theme.spacing.md};
`;

const ValuesSection = styled(Section)`
  background-color: ${theme.colors.backgroundAlt};
  padding: ${theme.spacing.xxl} 0;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  text-align: center;
  transition: ${theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.medium};
    
    svg {
      color: ${theme.colors.primary};
      transform: scale(1.1);
    }
  }
  
  svg {
    font-size: 2.5rem;
    color: ${theme.colors.secondary};
    margin-bottom: ${theme.spacing.md};
    transition: ${theme.transitions.medium};
  }
  
  h3 {
    margin-bottom: ${theme.spacing.sm};
    font-size: 1.25rem;
  }
  
  p {
    color: ${theme.colors.textLight};
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const TeamSection = styled(Section)`
  padding: ${theme.spacing.xxl} 0;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TeamMember = styled.div`
  text-align: center;
`;

const TeamMemberImage = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto ${theme.spacing.md};
  border: 3px solid ${theme.colors.primary};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TeamMemberName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.xs};
`;

const TeamMemberTitle = styled.p`
  color: ${theme.colors.primary};
  font-weight: 500;
  margin-bottom: ${theme.spacing.sm};
`;

const TeamMemberBio = styled.p`
  color: ${theme.colors.textLight};
  line-height: 1.6;
`;

const AboutPage: React.FC = () => {
  return (
    <>
      <PageHero
        title="About Alherz Perfume"
        subtitle="Crafting exquisite fragrances that capture the essence of Arabian luxury since 2005"
        height="40vh"
      />
      
      <StorySection>
        <Container>
          <SectionTitle>Our Story</SectionTitle>
          <StoryContent>
            <StoryParagraph>
              Founded in 2005 in the heart of Bahrain, Alherz Perfume began as a small boutique with a passion for authentic Arabian fragrances. Our founder, Ahmed Al-Herz, came from a long line of perfumers and sought to blend traditional Arabian perfumery with modern luxury.
            </StoryParagraph>
            <StoryParagraph>
              What started as a single store in Manama has grown into Bahrain's premier destination for luxury fragrances, with a reputation that extends throughout the Gulf region. Throughout our journey, we have remained committed to sourcing the finest ingredients from around the world and working with master perfumers to create scents that are both timeless and distinctive.
            </StoryParagraph>
            <StoryParagraph>
              Today, Alherz Perfume offers an extensive collection of premium fragrances, oud oils, and luxury gift sets. Each product is carefully crafted to meet our exacting standards, ensuring that every customer experiences the perfect blend of tradition and innovation.
            </StoryParagraph>
          </StoryContent>
        </Container>
      </StorySection>
      
      <ValuesSection>
        <Container>
          <SectionTitle>Our Values</SectionTitle>
          <SectionSubtitle>
            These core principles guide everything we do at Alherz Perfume
          </SectionSubtitle>
          
          <ValuesGrid>
            <ValueCard>
              <FontAwesomeIcon icon={faGem} />
              <h3>Quality</h3>
              <p>We source only the finest ingredients and maintain rigorous quality standards throughout our production process.</p>
            </ValueCard>
            <ValueCard>
              <FontAwesomeIcon icon={faHandshake} />
              <h3>Integrity</h3>
              <p>We believe in transparent business practices and building lasting relationships with our customers and partners.</p>
            </ValueCard>
            <ValueCard>
              <FontAwesomeIcon icon={faLeaf} />
              <h3>Sustainability</h3>
              <p>We are committed to responsible sourcing and minimizing our environmental impact through eco-friendly practices.</p>
            </ValueCard>
            <ValueCard>
              <FontAwesomeIcon icon={faAward} />
              <h3>Excellence</h3>
              <p>We strive for excellence in every aspect of our business, from customer service to product innovation.</p>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </ValuesSection>
      
      <TeamSection>
        <Container>
          <SectionTitle>Our Team</SectionTitle>
          <SectionSubtitle>
            Meet the passionate individuals behind Alherz Perfume
          </SectionSubtitle>
          
          <TeamGrid>
            <TeamMember>
              <TeamMemberImage>
                <img src="https://images.unsplash.com/photo-1566492031773-4f4e44671857?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80" alt="Ahmed Al-Herz" />
              </TeamMemberImage>
              <TeamMemberName>Ahmed Al-Herz</TeamMemberName>
              <TeamMemberTitle>Founder & Master Perfumer</TeamMemberTitle>
              <TeamMemberBio>
                With over 25 years of experience in perfumery, Ahmed's passion for fragrances has been the driving force behind Alherz Perfume's success.
              </TeamMemberBio>
            </TeamMember>
            
            <TeamMember>
              <TeamMemberImage>
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80" alt="Layla Al-Herz" />
              </TeamMemberImage>
              <TeamMemberName>Layla Al-Herz</TeamMemberName>
              <TeamMemberTitle>Creative Director</TeamMemberTitle>
              <TeamMemberBio>
                Layla oversees product development and brand identity, ensuring that every Alherz creation reflects our commitment to luxury and authenticity.
              </TeamMemberBio>
            </TeamMember>
            
            <TeamMember>
              <TeamMemberImage>
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80" alt="Khalid Rahman" />
              </TeamMemberImage>
              <TeamMemberName>Khalid Rahman</TeamMemberName>
              <TeamMemberTitle>Head of Operations</TeamMemberTitle>
              <TeamMemberBio>
                Khalid ensures that our supply chain and customer experience meet the highest standards of excellence and efficiency.
              </TeamMemberBio>
            </TeamMember>
          </TeamGrid>
        </Container>
      </TeamSection>
    </>
  );
};

export default AboutPage; 